import { EnumSchema, Executor, ExecutorSchema, FromSchema } from '@40seas/entities/common';
import { BaseEntity, WithBaseEntitySchema } from '@40seas/models';
import { z } from 'zod';
import { DecisionSource } from './decision-source.enum';
import { IdentityCandidate, IdentityCandidateSchema, ScoreBreakdown, ScoreBreakdownSchema } from './identity-candidate.entity';
import { IdentityStatus } from './identity-status.enum';

export const IdentityDecisionSchema = WithBaseEntitySchema(
    z
        .object({
            identity: z.string().openapi({ description: 'Reference to the identity entity this decision applies to' }),
            buyer: z.string().openapi({ description: 'Reference to the buyer entity associated with this identity decision' }),
            status: EnumSchema(IdentityStatus).openapi({
                description:
                    'Verification status of the identity. Verified: identity has been confirmed and approved. Unverified: identity could not be verified. PossibleMatch: single candidate requires confirmation. ReviewRequired: multiple candidates need selection.',
            }),
            decisionSource: EnumSchema(DecisionSource).nullish().openapi({
                description:
                    'Source of the decision. AutoByRegistration/AutoByName: automatically determined. ManualSelection: user selected from candidates. ManualConfirmation: user confirmed single candidate. Null when decision has been reset.',
            }),
            /**
             * The selected/confirmed candidate (full object).
             * Set when status is 'verified' or 'possible-match', or when user selects from candidates.
             * Null for 'unverified' status or when no candidate has been selected yet.
             */
            selectedCandidate: IdentityCandidateSchema.nullish().openapi({
                description:
                    "The selected/confirmed candidate. Set when status is 'verified' or 'possible-match', or when user selects from candidates. May be null for 'unverified' status.",
            }),
            effectiveConfidence: z.number().nullish().openapi({
                description: 'Effective confidence score (0-100) from the identity matching algorithm',
            }),
            /** Score breakdown explaining how the confidence score was calculated */
            scoreBreakdown: ScoreBreakdownSchema.nullish().openapi({
                description: 'Breakdown explaining how the confidence score was calculated',
            }),
            approvedBy: ExecutorSchema.optional().openapi({ description: 'User or system that approved this identity decision' }),
            approvedAt: z
                .date()
                .optional()
                .openapi({ format: 'date-time', type: 'string', description: 'Timestamp when the decision was approved' }),
            notes: z
                .string()
                .optional()
                .openapi({ description: 'Additional notes or comments regarding the identity verification decision' }),
        })
        .openapi({
            description:
                'Decision record for identity verification. Captures whether a business identity has been verified, who made the decision, and any relevant context',
        }),
).strict();

export class IdentityDecision extends FromSchema(IdentityDecisionSchema) implements BaseEntity {
    id: string;
    identity: string;
    buyer: string;
    status: IdentityStatus;
    decisionSource?: DecisionSource | null;
    selectedCandidate?: IdentityCandidate | null;
    effectiveConfidence?: number | null;
    scoreBreakdown?: ScoreBreakdown | null;
    approvedBy?: Executor;
    approvedAt?: Date;
    notes?: string;

    tenantId: string;
    seller?: string;
    createdAt?: Date;
    updatedAt?: Date;
    createdBy?: Executor;
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}
